import { AuthBaseClient } from '../base-client';
import type { SearchResponse } from '@vagusx/shared';
import type {
  TaskAssignmentRuleResponse,
  CreateTaskAssignmentRuleRequest,
  UpdateTaskAssignmentRuleRequest,
  ReorderAssignmentRulesRequest,
  AssignmentEvaluation,
} from './types';

/**
 * Client for task assignment rule API endpoints.
 */
export class TaskAssignmentRuleClient extends AuthBaseClient {
  /**
   * List the org's priority-ordered regular rules. The catch-all is
   * fetched via `getCatchAll()` — the two subsets are disjoint and live
   * on separate endpoints so the priority list response stays a clean
   * paginated shape and the catch-all stays a single-row read.
   */
  async list(): Promise<SearchResponse<TaskAssignmentRuleResponse>> {
    return this.get<SearchResponse<TaskAssignmentRuleResponse>>(`/api/task-assignment-rules`);
  }

  /**
   * Get the org's catch-all rule (zero or one). Returns `{ rule: null }`
   * when no catch-all is configured.
   */
  async getCatchAll(): Promise<{ rule: TaskAssignmentRuleResponse | null }> {
    return this.get<{ rule: TaskAssignmentRuleResponse | null }>(
      `/api/task-assignment-rules/catch-all`
    );
  }

  /**
   * Get a single rule by ID.
   */
  async getById(id: string): Promise<TaskAssignmentRuleResponse> {
    return this.get<TaskAssignmentRuleResponse>(`/api/task-assignment-rules/${id}`);
  }

  /**
   * Create a new assignment rule.
   */
  async create(data: CreateTaskAssignmentRuleRequest): Promise<TaskAssignmentRuleResponse> {
    return this.post<TaskAssignmentRuleResponse>('/api/task-assignment-rules', data);
  }

  /**
   * Update an existing assignment rule.
   */
  async update(
    id: string,
    data: UpdateTaskAssignmentRuleRequest
  ): Promise<TaskAssignmentRuleResponse> {
    return this.patch<TaskAssignmentRuleResponse>(`/api/task-assignment-rules/${id}`, data);
  }

  /**
   * Delete an assignment rule (soft delete).
   */
  async deleteRule(id: string): Promise<void> {
    return this.delete<void>(`/api/task-assignment-rules/${id}`);
  }

  /**
   * Reorder rules (update priorities).
   */
  async reorder(data: ReorderAssignmentRulesRequest): Promise<void> {
    return this.post<void>('/api/task-assignment-rules/reorder', data);
  }

  /**
   * Evaluate rules against a specific task (dry run).
   */
  async evaluate(taskId: string): Promise<AssignmentEvaluation> {
    return this.post<AssignmentEvaluation>(`/api/task-assignment-rules/evaluate/${taskId}`);
  }
}
